import useSuitesProjectsLanguage from '../useSuitesProjectsLanguage';
import React from 'react';
import { CheckCircle, XCircle, AlertCircle, Workflow, FlaskConical, X } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';

import StatusIndicator from './StatusIndicator';

export default function SuiteRunResultsDialog({
  showRunResultModal,
  selectedRun,
  formatRelativeTime,
  setShowRunResultModal,
  setSelectedRun
}) {
  const { t } = useSuitesProjectsLanguage();
  return (<>
      {/* Run Result Modal */}
      {showRunResultModal && selectedRun && (
        <div className="lexa-suite-overlay fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="lexa-suite-dialog lexa-suite-dialog--results w-full max-w-2xl p-0 max-h-[80vh] flex flex-col overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <StatusIndicator status={selectedRun.status} size="lg" />
                <div>
                  <h2 className="font-semibold">{t("Run Results")}</h2>
                  <p className="text-sm text-gray-500">{formatRelativeTime(selectedRun.started_at)}</p>
                </div>
              </div>
              <Button variant="ghost" size="sm" aria-label={t("Close")} onClick={() => {
                setShowRunResultModal(false);
                setSelectedRun(null);
              }}>
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-4 gap-3 p-4 bg-gray-50 border-b">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">{selectedRun.summary?.total || 0}</div>
                <div className="text-xs text-gray-500">{t("Total")}</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{selectedRun.summary?.passed || 0}</div>
                <div className="text-xs text-green-600">{t("Passed")}</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">{selectedRun.summary?.failed || 0}</div>
                <div className="text-xs text-red-600">{t("Failed")}</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-600">{selectedRun.summary?.skipped || 0}</div>
                <div className="text-xs text-yellow-600">{t("Skipped")}</div>
              </div>
            </div>

            {/* Test Results */}
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {selectedRun.test_results?.map((result, index) => (
                <div
                  key={result.test_id}
                  className={`p-3 rounded-lg border ${
                    result.status === 'passed' ? 'bg-green-50 border-green-200' :
                    result.status === 'failed' ? 'bg-red-50 border-red-200' :
                    result.status === 'skipped' ? 'bg-yellow-50 border-yellow-200' :
                    'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="w-5 h-5 flex items-center justify-center rounded-full bg-white text-xs font-medium mr-2">
                        {index + 1}
                      </span>
                      {result.test_type === 'api_test' ? (
                        <FlaskConical className="w-4 h-4 mr-2 text-blue-600" />
                      ) : (
                        <Workflow className="w-4 h-4 mr-2 text-purple-600" />
                      )}
                      <span className="font-medium text-sm">{result.test_name}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-500">{result.duration_ms}ms</span>
                      {result.status === 'passed' ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : result.status === 'failed' ? (
                        <XCircle className="w-4 h-4 text-red-600" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-yellow-600" />
                      )}
                    </div>
                  </div>
                  {result.error && (
                    <div className="mt-2 p-2 bg-white rounded text-xs text-red-600 font-mono overflow-x-auto">
                      {result.error}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}
  </>);
}
